import type { VoidFunctionComponent } from 'react';

import useAddDependency from '../../../hooks/api/dependencies/useAddDependency';
import Tabs, { useTabs } from '../../generic/Tabs';

import CreateDependency from './CreateDependency';
import useUploadAndBuildArchiveDependency from './hooks/useUploadAndBuildArchiveDependency';
import SearchDependency from './SearchDependency';
import UploadDependency from './UploadDependency';

import styles from './index.module.less';

export type AddDependencyDrawerProps = {
  /**
   * Locator of the user revision to which the dependency is being added.
   */
  userRevisionLocator: string;
  /**
   * Locator of the existing dependency when overwriting.
   */
  overwriteDependencyLocator?: string;
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;
  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

const tabIds = ['Search', 'Upload', 'Create'] as const;
type TabId = typeof tabIds[number];

/**
 * Drawer component for adding a dependency (manual or vendored). Manual dependencies
 * can be added via one of three workflows: Search, Upload, or Create.
 */
const AddDependencyDrawer: VoidFunctionComponent<AddDependencyDrawerProps> = ({
  userRevisionLocator,
  overwriteDependencyLocator,
  isVendored,
  onClose,
}) => {
  const { tabs, activeTab } = useTabs<TabId>({ tabIds });

  const {
    mutateAsync: uploadAndBuildArchive,
    isPending: isUploading,
  } = useUploadAndBuildArchiveDependency(userRevisionLocator);
  const { mutateAsync: addDependency, isPending: isSaving } = useAddDependency(userRevisionLocator);

  return (
    <div className={styles.drawer}>
      <h2 className={styles.header}>{overwriteDependencyLocator ? 'Overwrite' : 'Add'} {isVendored ? 'vendored' : 'manual'} dependency</h2>
      {overwriteDependencyLocator && (
        <div className={styles.overwrite}>
          <p>This action will overwrite the following dependency in your project:</p>
          <p className={styles.overwriteLocator}>{overwriteDependencyLocator}</p>
        </div>
      )}
      <Tabs>
        {tabs.map(tab => (
          <Tabs.Tab
            key={tab.label}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...tab}
          />
        ))}
      </Tabs>
      <div className={styles.content}>
        {activeTab === 'Search' && (
          <SearchDependency
            isVendored={isVendored}
            overwriteDependencyLocator={overwriteDependencyLocator}
            saveDependency={addDependency}
            isSaving={isSaving}
            onClose={onClose}
          />
        )}
        {activeTab === 'Upload' && (
          <UploadDependency
            isVendored={isVendored}
            overwriteDependencyLocator={overwriteDependencyLocator}
            uploadAndBuildDependency={uploadAndBuildArchive}
            isUploading={isUploading}
            saveDependency={addDependency}
            isSaving={isSaving}
            onClose={onClose}
          />
        )}
        {activeTab === 'Create' && (
          <CreateDependency
            isVendored={isVendored}
            overwriteDependencyLocator={overwriteDependencyLocator}
            saveDependency={addDependency}
            isSaving={isSaving}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default AddDependencyDrawer;
